import React from 'react'
import { useFormContext, FieldError, NestDataObject } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useStateMachine } from 'little-state-machine'

import {
  Instruction1P,
  Instruction1T,
  Instruction2P,
  Instruction2T,
} from '../Fields/Instructions'
import { Matrix } from '../Fields/RadioGroup'
import { Makaton } from '../Fields/Makaton'
import {
  PersonFillingScaleWithOptions,
  PersonFillingScaleWithOptionsAlt,
} from '../Fields/PersonFillingScaleWithOptions'

import { Label, FieldWrapper } from '../Layout'
import { LanguageSelect } from './LanguageSelection'
import { MAX_LENGTH, CharCounter } from '../CharCounter'
import { FieldType } from '../types'
import { InquiryFormType } from '../../../utils/constants'
import { Store } from '../Scale'

type Props = {
  activeQuestion: FieldType
  formType: InquiryFormType['name']
  errors: NestDataObject<Record<string, string>, FieldError>
  allMatrixQuestionsByScaleType: string[]
}

export function Field({
  activeQuestion,
  formType,
  allMatrixQuestionsByScaleType,
}: Props) {
  const { register, watch, setValue } = useFormContext()
  const { t } = useTranslation('translation')

  const {
    state: { inquery },
  } = useStateMachine<Store>()


  React.useEffect(() => {
    if (
      activeQuestion.type === 'text' ||
      activeQuestion.type === 'number' ||
      activeQuestion.type === 'select'
    ) {
      if (inquery.defaultValues[activeQuestion.name]) {
        setValue(
          activeQuestion.name,
          inquery.defaultValues[activeQuestion.name]
        )
      }
    }
  }, [activeQuestion, inquery.defaultValues])

  switch (activeQuestion.type) {
    case 'text': {
      const fieldValue = watch(activeQuestion.name)

      return (
        <FieldWrapper>
          <Label>
            {t(activeQuestion.title)}
            <textarea
              key={activeQuestion.name}
              name={activeQuestion.name}
              ref={register({
                required: activeQuestion.optional ? false : 'required',
              })}
              maxLength={MAX_LENGTH}
            />
          </Label>

          <CharCounter length={fieldValue?.length} />
        </FieldWrapper>
      )
    }

    case 'number':
      return (
        <FieldWrapper>
          <Label>
            {t(`${activeQuestion.title}`)}

            <input
              min="0"
              key={activeQuestion.name}
              name={activeQuestion.name}
              type="number"
              ref={register({ required: 'required' })}
            />
          </Label>
        </FieldWrapper>
      )

    case 'matrix':
      return (
        <Matrix
          answers={activeQuestion.answers}
          questions={activeQuestion.questions}
          title={activeQuestion.title}
          formType={formType}
          allMatrixQuestionsByScaleType={allMatrixQuestionsByScaleType}
        />
      )

    case 'select':
      return (
        <FieldWrapper>
          <Label htmlFor={`label-for-${activeQuestion.name}`}>
            {t(activeQuestion.title)}

            <select
              id={`select-for-${activeQuestion.name}`}
              key={activeQuestion.name}
              name={activeQuestion.name}
              ref={register({ required: 'required' })}
            >
              {activeQuestion.options.map((option, index) => (
                //index + 1, easy way to make it start from 1 instead of 0
                <option key={option} value={index + 1}>
                  {t(`options.${option}`)}
                </option>
              ))}
            </select>
          </Label>
        </FieldWrapper>
      )

    case 'languageSelection':
      return <LanguageSelect />

    case 'instruction1P':
      return <Instruction1P />
    case 'instruction1T':
      return <Instruction1T />
    case 'instruction2P':
      return <Instruction2P />
    case 'instruction2T':
      return <Instruction2T />

    case 'makaton':
      return <Makaton />
    case 'personFillingScaleWithOptions':
      return <PersonFillingScaleWithOptions />
    case 'personFillingScaleWithOptionsAlt':
      return <PersonFillingScaleWithOptionsAlt />
  }
}
